/**
 * The provider build flow surface: a per-step timeline and the "builds in progress" panel.
 *
 * A SEPARATE surface from the versions list, deliberately: isVersionVisible (worker) still hides
 * a version until a completed scan exists - nothing unverified ever appears as a version. An
 * in-flight build appears HERE, clearly as pipeline state, driven by the Argo-derived flow
 * (supervisor-pushed snapshot on the list; a live read behind the per-version endpoint).
 *
 * `unknown` renders as "state unavailable" - never as pending/queued. That distinction is the
 * whole point of the flow design: a tunnel blip and a lost fleet must not look the same.
 */
import { useEffect, useState } from 'react'
import { Badge, type BadgeVariant } from '@/shared/components/Badge'
import { Tooltip } from '@/shared/components/Tooltip'
import type { ProviderFlowItem, ProviderFlowPhase, ProviderFlowState, ProviderFlowStep } from '@/lib/apiTypes'
import { useProviderFlows, useProviderVersionFlow } from '@/lib/hooks'

const PHASE_BADGE: Record<ProviderFlowPhase, { label: string; variant: BadgeVariant }> = {
  none:      { label: 'not requested', variant: 'neutral' },
  queued:    { label: 'queued', variant: 'neutral' },
  running:   { label: 'building', variant: 'amber' },
  blocked:   { label: 'blocked by gates', variant: 'red' },
  succeeded: { label: '✓ built', variant: 'green' },
  failed:    { label: 'failed', variant: 'red' },
  lost:      { label: 'lost', variant: 'red' },
  unknown:   { label: 'state unavailable', variant: 'neutral' },
}

export function FlowPhaseBadge({ phase }: { phase: ProviderFlowPhase }) {
  const cfg = PHASE_BADGE[phase] ?? PHASE_BADGE.unknown
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>
}

const STEP_LABEL: Record<string, string> = {
  patch: 'patch', pin: 'pin', compile: 'compile', g124: 'gates', g3: 'malware scan', finalize: 'sign + publish',
}

function stepColor(outcome: ProviderFlowStep['outcome']): string {
  switch (outcome) {
    case 'ok': return 'var(--green)'
    case 'failed': return 'var(--red)'
    case 'in_progress': return 'var(--amber)'
    case 'skipped': return 'var(--text-tertiary)'
    default: return 'var(--border)'
  }
}

function ago(ms: number | null | undefined): string {
  if (!ms) return ''
  const s = Math.max(0, Math.round((Date.now() - ms) / 1000))
  if (s < 60) return `${s}s ago`
  if (s < 3600) return `${Math.round(s / 60)}m ago`
  return `${(s / 3600).toFixed(1)}h ago`
}

/** The step waterfall: one dot+label per DAG step in enum order, retries collapsed into their
 *  step's latest attempt (the attempt count rides the tooltip). */
export function BuildFlowTimeline({ flow }: { flow: ProviderFlowState }) {
  // Latest attempt per step wins the dot; the enum order arrives server-side.
  const byStep = new Map<string, ProviderFlowStep>()
  for (const s of flow.steps) {
    const prior = byStep.get(s.step)
    if (!prior || s.attempt >= prior.attempt) byStep.set(s.step, s)
  }
  const steps = [...byStep.values()]
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
      {steps.map((s, i) => (
        <div key={s.step} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          {i > 0 && <span style={{ width: 12, height: 1, background: 'var(--border)' }} />}
          <Tooltip
            text={[
              s.outcome === 'pending' ? 'not reached yet' : s.outcome.replace('_', ' '),
              s.attempt > 0 ? `attempt ${s.attempt + 1}` : '',
              s.message ?? '',
            ].filter(Boolean).join(' · ')}
            placement="top"
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: s.outcome === 'pending' ? 'var(--text-tertiary)' : 'var(--text-secondary)' }}>
              <span aria-hidden style={{
                width: 8, height: 8, borderRadius: '50%',
                background: stepColor(s.outcome),
                ...(s.outcome === 'pending' ? { background: 'transparent', border: '1px solid var(--border)' } : {}),
              }} />
              {STEP_LABEL[s.step] ?? s.step}
            </span>
          </Tooltip>
        </div>
      ))}
    </div>
  )
}

function FlowRow({ orgId, item }: { orgId: string; item: ProviderFlowItem }) {
  const [open, setOpen] = useState(false)
  // Live detail on expand only: the list stays snapshot-backed and cheap.
  const detail = useProviderVersionFlow(
    orgId,
    open ? item.upstream : null, open ? item.namespace : null,
    open ? item.type : null, open ? item.version : null,
  )
  const flow = detail.data?.flow ?? item.flow
  return (
    <div style={{ padding: '8px 12px', borderTop: '1px solid var(--border)' }}>
      <button
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="btn-unstyled"
        style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
      >
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13 }}>
          {item.namespace}/{item.type} <span style={{ color: 'var(--text-secondary)' }}>{item.version}</span>
        </span>
        <FlowPhaseBadge phase={flow.phase} />
        {flow.current_step && (
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            on {STEP_LABEL[flow.current_step] ?? flow.current_step}
          </span>
        )}
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-tertiary)' }}>
          {flow.phase === 'unknown'
            ? `last seen ${ago(flow.observed_at) || 'never'}`
            : `started ${ago(item.triggered_at)}`}
        </span>
      </button>
      {open && (
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <BuildFlowTimeline flow={flow} />
          {flow.failure_reason && (
            <div style={{ fontSize: 12, color: flow.blocked ? 'var(--text-secondary)' : 'var(--red)', fontFamily: 'var(--font-mono)' }}>
              {flow.failure_stage ? `${flow.failure_stage}: ` : ''}{flow.failure_reason}
            </div>
          )}
          {flow.phase === 'unknown' && (
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
              The build pipeline’s observer is not reporting right now; this is the last known state, not a verdict.
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** Builds in progress, plus the last 15 minutes of outcomes. Renders nothing when there is
 *  nothing to show - most orgs most of the time. Polls while anything is in flight. */
export function BuildsInProgressPanel({ orgId }: { orgId: string }) {
  const flows = useProviderFlows(orgId)
  const items = flows.data?.flows ?? []
  const anyLive = items.some((f) => ['queued', 'running', 'unknown'].includes(f.flow.phase))
  useEffect(() => {
    if (!anyLive) return
    const t = setInterval(() => { void flows.refetch() }, 20_000)
    return () => clearInterval(t)
  }, [anyLive, flows.refetch])
  if (items.length === 0) return null
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 20, background: 'var(--bg-secondary)' }}>
      <div style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13 }}>Builds in progress</span>
        <span style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
          pipeline state; versions appear in the list below only once verified
        </span>
      </div>
      {items.map((f) => <FlowRow key={f.job_id} orgId={orgId} item={f} />)}
    </div>
  )
}
